// Port of `Songs` (SuiteModel/Songs.swift; `design-docs/Musicals, radio and sitcoms — design.md`): a direction whose first
// line starts `Song:` names a song where it begins. Songs are numbered through the show, a reprise like any other, and
// sung by the cues whose speeches have lyrics before the next song, a transition or the scene's end (all the cues there
// when none has lyrics).
import { paragraphs as fountainParagraphs, paragraphText, speakersOfCue, type Paragraph } from './fountain.ts';

export interface Song {
  /** Its number through the show, from 1. */
  number: number;
  /** As written after `Song:` ("The River's High (Reprise)"). */
  title: string;
  /** Who sings it, in capitals, in order of first singing. */
  singers: string[];
}

/** `Song.heading`: "#3 — THE RIVER'S HIGH". */
export const songHeading = (s: Song): string => `#${s.number} — ${s.title.toUpperCase()}`;

function unforced(line: string): string {
  let t = line.replace(/^[ \t]+|[ \t]+$/g, '');
  if (t.startsWith('!')) t = t.slice(1);
  return t;
}

/** `Songs.title(ofLine:)`: the song a line names, else null. */
export function songTitle(line: string): string | null {
  const t = unforced(line);
  if ([...t].length <= 5 || t.slice(0, 5).toLowerCase() !== 'song:') return null;
  const title = t.slice(5).replace(/^[ \t]+|[ \t]+$/g, '');
  return title === '' ? null : title;
}

/** `Songs.isSongLine`: whether a line starts with the label, title or not. */
export const isSongLine = (line: string): boolean => unforced(line).slice(0, 5).toLowerCase() === 'song:';

/** `Songs.isReprise`. */
export const isReprise = (title: string): boolean => title.toLowerCase().includes('(reprise)');

/** `Songs.title(of:)`: the song a paragraph begins. */
export function songOfParagraph(p: Paragraph): string | null {
  if (p.element !== 'action' || p.lines.length === 0) return null;
  return songTitle(p.lines[0].map((r) => r.text).join(''));
}

/** `Songs.songs(in:)`: the songs a scene's paragraphs begin, with each paragraph's index and singers. */
export function songsIn(paragraphs: readonly Paragraph[]): { index: number; title: string; singers: string[] }[] {
  const starts: [number, string][] = [];
  paragraphs.forEach((p, i) => { const t = songOfParagraph(p); if (t !== null) starts.push([i, t]); });
  return starts.map(([index, title], n) => {
    const end = n + 1 < starts.length ? starts[n + 1][0] : paragraphs.length;
    return { index, title, singers: singers(paragraphs.slice(index + 1, end)) };
  });
}

function singers(span: readonly Paragraph[]): string[] {
  const withLyrics: string[] = [], all: string[] = [];
  let cue: string[] = [];
  const pick = () => (withLyrics.length ? withLyrics : all);
  for (const p of span) {
    switch (p.element) {
      case 'transition': case 'pageBreak': case 'sceneHeading': return pick();
      case 'character':
        cue = speakersOfCue(paragraphText(p));
        for (const name of cue) if (!all.includes(name)) all.push(name);
        break;
      case 'lyric':
        for (const name of cue) if (!withLyrics.includes(name)) withLyrics.push(name);
        break;
      case 'dialogue': case 'parenthetical': break;
      default: cue = [];
    }
  }
  return pick();
}

/** `Songs.numbered`: every song in a show's scenes (Fountain, reading order), numbered through it. */
export function numberedSongs(scenes: readonly (readonly Paragraph[])[]): Song[] {
  const out: Song[] = [];
  for (const scene of scenes) for (const s of songsIn(scene)) out.push({ number: out.length + 1, title: s.title, singers: s.singers });
  return out;
}

/** `ScriptSpeeches.songs`: each singer's songs, most first (then first to sing), and the show's songs. */
export function songsByCharacter(texts: readonly string[]): { singers: { name: string; songs: number }[]; total: number } {
  const counts = new Map<string, number>();
  let total = 0;
  for (const text of texts) {
    for (const song of songsIn(fountainParagraphs(text))) {
      total += 1;
      for (const name of song.singers) counts.set(name, (counts.get(name) ?? 0) + 1);
    }
  }
  const order = [...counts.keys()];
  return { singers: order.map((name) => ({ name, songs: counts.get(name)! })).sort((a, b) => b.songs - a.songs || order.indexOf(a.name) - order.indexOf(b.name)), total };
}
